import {
  getAcademicCalendarUrl,
  getBuildingsUrl,
  getEducationClassesUrl,
  getEducationPlansUrl,
  getHomeUrl,
  getLogoutUrl,
  getMunicipalitiesUrl,
  getOrganizationsUrl,
  getProductionCalendarUrl,
  getRegionsUrl,
  getScheduleForWeekUrl,
  getSubjectDifficultiesUrl,
  getBellSchedulesUrl,
  getMaxLessonsPerDayUrl,
  getTeacherWorkloadsUrl,
  getClassGroupsUrl,
} from 'services/navigation';
import { makeKeyGenerator } from 'shared/helpers/utils/key-generator';
import { AppHomeIcon } from 'shared/UI/UIKit/icons/Home';
import { useAuth } from 'shared/services/auth';
import { useProfileRole } from 'common/use-profile-role';
import { AppBooksIcon } from 'shared/UI/UIKit/icons/Books';
import { AppMenuIcon } from 'shared/UI/UIKit/icons/Menu';
import { AppScheduleIcon } from 'shared/UI/UIKit/icons/Schedule';
import { AppLogoutIcon } from 'shared/UI/UIKit/icons/Logout';
import { SidebarMenuHelpers } from '../helpers';
import styles from './styles.module.scss';

const HOME_URL = getHomeUrl();
const REGIONS_URL = getRegionsUrl();
const MUNICIPALITIES_URL = getMunicipalitiesUrl();
const ORGANIZATIONS_URL = getOrganizationsUrl();
const BUILDINGS_URL = getBuildingsUrl();
const EDUCATION_CLASSES_URL = getEducationClassesUrl();
const EDUCATION_PLANS_URL = getEducationPlansUrl();
const LOGOUT_URL = getLogoutUrl();
const ACADEMIC_CALENDAR_URL = getAcademicCalendarUrl();
const PRODUCTION_CALENDAR_URL = getProductionCalendarUrl();
const SUBJECT_DIFFICULTIES_URL = getSubjectDifficultiesUrl();
const SCHEDULE_FOR_WEEK_URL = getScheduleForWeekUrl();
const TEACHER_WORKLOADS_URL = getTeacherWorkloadsUrl();
const CLASS_GROUPS_URL = getClassGroupsUrl();
const BELL_SCHEDULES_URL = getBellSchedulesUrl();
const MAX_LESSONS_PER_DAY_URL = getMaxLessonsPerDayUrl();

export const useSidebar = (visible: boolean) => {
  const { getNextKey } = makeKeyGenerator();
  const { logout } = useAuth();
  const { isAdmin, isManager } = useProfileRole();

  const getSidebarMenuItems = () => {
    if (!visible) {
      return [];
    }

    return [
      SidebarMenuHelpers.getLinkItem({
        key: getNextKey(),
        label: 'Главная',
        icon: <AppHomeIcon />,
        linkProps: {
          to: HOME_URL,
        },
      }),
      SidebarMenuHelpers.getDividerItem(),
      ...(isAdmin
        ? [
            SidebarMenuHelpers.getSubMenuItem({
              key: getNextKey(),
              label: 'Справочники и реестры',
              icon: <AppBooksIcon />,
              children: [
                SidebarMenuHelpers.getLinkItem({
                  key: getNextKey(),
                  label: 'Регионы',
                  className: styles.subMenuItem,
                  linkProps: {
                    to: REGIONS_URL,
                  },
                }),
                SidebarMenuHelpers.getLinkItem({
                  key: getNextKey(),
                  label: 'Муниципалитеты',
                  className: styles.subMenuItem,
                  linkProps: {
                    to: MUNICIPALITIES_URL,
                  },
                }),
                SidebarMenuHelpers.getLinkItem({
                  key: getNextKey(),
                  label: 'Организации',
                  className: styles.subMenuItem,
                  linkProps: {
                    to: ORGANIZATIONS_URL,
                  },
                }),
                SidebarMenuHelpers.getLinkItem({
                  key: getNextKey(),
                  label: 'Сложность предметов',
                  className: styles.subMenuItem,
                  linkProps: {
                    to: SUBJECT_DIFFICULTIES_URL,
                  },
                }),
              ],
            }),
            SidebarMenuHelpers.getDividerItem(),
          ]
        : []),
      ...(isManager
        ? [
            SidebarMenuHelpers.getSubMenuItem({
              key: getNextKey(),
              label: 'Справочники и реестры',
              icon: <AppBooksIcon />,
              children: [
                SidebarMenuHelpers.getLinkItem({
                  key: getNextKey(),
                  label: 'Расписание звонков',
                  className: styles.subMenuItem,
                  linkProps: {
                    to: BELL_SCHEDULES_URL,
                  },
                }),
                SidebarMenuHelpers.getLinkItem({
                  key: getNextKey(),
                  label: 'Количество уроков',
                  className: styles.subMenuItem,
                  linkProps: {
                    to: MAX_LESSONS_PER_DAY_URL,
                  },
                }),
                SidebarMenuHelpers.getLinkItem({
                  key: getNextKey(),
                  label: 'Классы',
                  className: styles.subMenuItem,
                  linkProps: {
                    to: EDUCATION_CLASSES_URL,
                  },
                }),
                SidebarMenuHelpers.getLinkItem({
                  key: getNextKey(),
                  label: 'Здания и аудитории',
                  className: styles.subMenuItem,
                  linkProps: {
                    to: BUILDINGS_URL,
                  },
                }),
                SidebarMenuHelpers.getLinkItem({
                  key: getNextKey(),
                  label: 'Календарно-учебный график',
                  className: styles.subMenuItem,
                  linkProps: {
                    to: ACADEMIC_CALENDAR_URL,
                  },
                }),
                SidebarMenuHelpers.getLinkItem({
                  key: getNextKey(),
                  label: 'Производственный календарь',
                  className: styles.subMenuItem,
                  linkProps: {
                    to: PRODUCTION_CALENDAR_URL,
                  },
                }),
              ],
            }),
            SidebarMenuHelpers.getDividerItem(),
            SidebarMenuHelpers.getSubMenuItem({
              key: getNextKey(),
              label: 'Группы по предметам',
              icon: <AppMenuIcon />,
              children: [
                SidebarMenuHelpers.getLinkItem({
                  key: getNextKey(),
                  label: 'Учебные планы классов',
                  className: styles.subMenuItem,
                  linkProps: {
                    to: EDUCATION_PLANS_URL,
                  },
                }),
                SidebarMenuHelpers.getLinkItem({
                  key: getNextKey(),
                  label: 'Нагрузка учителей',
                  className: styles.subMenuItem,
                  linkProps: {
                    to: TEACHER_WORKLOADS_URL,
                  },
                }),
                SidebarMenuHelpers.getLinkItem({
                  key: getNextKey(),
                  label: 'Учебные группы',
                  className: styles.subMenuItem,
                  linkProps: {
                    to: CLASS_GROUPS_URL,
                  },
                }),
              ],
            }),
            SidebarMenuHelpers.getDividerItem(),
            SidebarMenuHelpers.getSubMenuItem({
              key: getNextKey(),
              label: 'Расписание',
              icon: <AppScheduleIcon />,
              children: [
                SidebarMenuHelpers.getLinkItem({
                  key: getNextKey(),
                  label: 'Расписание на неделю',
                  className: styles.subMenuItem,
                  linkProps: {
                    to: SCHEDULE_FOR_WEEK_URL,
                  },
                }),
              ],
            }),
            SidebarMenuHelpers.getDividerItem(),
          ]
        : []),
      SidebarMenuHelpers.getLinkItem({
        key: getNextKey(),
        label: 'Выход',
        icon: <AppLogoutIcon />,
        linkProps: {
          to: LOGOUT_URL,
        },
        onClick: logout,
      }),
    ];
  };

  return {
    sidebarMenuItems: getSidebarMenuItems(),
  };
};
